import React, { useState } from "react";
import { Box, Modal } from "@mui/material";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router";
import { register } from "@redux/slices/auth";
import { RegisterDataSchema } from "@/schemas/auth";
import { enqueueSnackbar } from "notistack";
import { RegisterData } from "@typedefs/auth";
import { cn } from "@/lib/utils";
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

// Define the email schema with zod
const emailSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});

interface Props {
  showModal: boolean;
  handleCloseModal: () => void;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  borderRadius: "6px",
  backgroundColor: "#F4F4F4",
  color: "#3F3F46",
};

const AddNewUser: React.FC<Props> = ({ showModal, handleCloseModal }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [isPasswordVisible, setPasswordVisibility] = useState(false);

  function onHandleEye (){

    if(isPasswordVisible){
      setPasswordVisibility(false);
    }
    else{
      setPasswordVisibility(true);
    }
  
  }
  // Use react-hook-form to manage form state and validation
  const {
    register: registerInput, // `registerInput` to avoid conflict with `register` function
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(RegisterDataSchema), // Use Zod resolver for form validation
  });
  const [data, setData] = useState<RegisterData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    apikey: "",
  });
  const [error, setError] = useState<string | null>(null);

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    console.log(data);

    console.log("data was set" + data.apikey);
    try {
      // Validate the data with Zod schema
      RegisterDataSchema.parse(data);

      // Make the registration request
      await register(data, navigate).finally(() => {
        setLoading(false);
      });
      // dff
    } catch (error: any) {
      // Handle any errors in the form submission
      if (error?.errors?.[0]?.message) {
        setError(error.errors[0].message);
        enqueueSnackbar(error.errors[0].message, { variant: "error" });
      }
    }

    // Ensure that the loading state is turned off after completion
    setLoading(false);
  };

  return (
    <Modal
      open={showModal}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="px-8 py-16">
          <h2 className="text-gray font-bold text-[25px] mb-7">Add New User</h2>

          {/* Single form for all fields */}
          <form onSubmit={handleOnSubmit}>
            <div className=" flex justify-between items-center content-center">
              <div className="w-[45%]">
                <h2 className="text-gray font-bold text-lg mb-4">First Name</h2>
                <input
                  type="text"
                  className="flex mt-5  h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  value={data.firstName}
                  placeholder="First Name"
                  onChange={(e) =>
                    setData({ ...data, firstName: e.target.value })
                  }
                  required
                />
              </div>

              <div className="w-[45%]">
                <h2 className="text-gray font-bold text-lg mb-4">Last Name</h2>

                <input
                  type="text"
                  value={data.lastName}
                  className="flex  h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Last Name"
                  onChange={(e) =>
                    setData({ ...data, lastName: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className=" flex justify-between items-center content-center">
              <div className="w-[45%]">
                <h2 className="text-gray font-bold text-lg mt-5 mb-4">Email</h2>

                <input
                  type="email"
                  value={data.email}
                  className="flex mt-5  h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Email"
                  onChange={(e) => setData({ ...data, email: e.target.value })}
                  required
                />
              </div>

              <div className="relative w-[45%]">
                <h2 className="text-gray font-bold text-lg mt-5 mb-4">
                  Password
                </h2>

                <input
                  type={isPasswordVisible ? "password" : "text"}
                  value={data.password}
                  className="flex mt-5 h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Password"
                  onChange={(e) =>
                    setData({ ...data, password: e.target.value })
                  }
                  required
                />
                  {isPasswordVisible ? (
            <RemoveRedEyeIcon onClick={onHandleEye} className="cursor-pointer absolute bottom-[5px] right-[10px]"/>
          ) : (
            <VisibilityOffIcon  onClick={onHandleEye} className="cursor-pointer absolute bottom-[5px] right-[10px]"/>
          )}
              </div>
            </div>

            <h2 className="text-gray font-bold text-lg mt-5 mb-4">Api Key</h2>

            <input
              type="text"
              value={data.apikey}
              className="flex mt-5  h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Api Key"
              onChange={(e) => setData({ ...data, apikey: e.target.value })}
              required
            />

            {error && <p style={{ color: "red" }}>{error}</p>}
            {/* {errors.password && <p>{errors.password.message}</p>} */}

            {/* Submit button */}
            <Button className="w-full mt-8" type="submit" disabled={loading}>
              {loading ? "Signing up..." : "Sign Up"}
            </Button>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default AddNewUser;
